import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, Compass, ChevronRight, Plus } from "lucide-react";

const COMMISSION_NEXT = {
  proposed: ["accrued", "held", "reversed"],
  accrued: ["eligible", "held", "reversed"],
  eligible: ["approved", "held", "disputed", "reversed"],
  approved: ["scheduled", "held", "disputed", "reversed"],
  scheduled: ["paid", "held", "reversed"],
  paid: ["clawed_back"],
  held: ["accrued", "eligible", "approved", "reversed"],
  disputed: ["approved", "reversed"],
};

export function AdminCommissions() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => api.get(`/admin/commissions${filter ? `?status=${filter}` : ""}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [filter]);

  const transition = async (id, status) => {
    try {
      await api.post(`/admin/commissions/${id}/transition`, { status });
      toast.success(`Moved to ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't transition.");
    }
  };

  if (!data) return <div className="text-white/40 p-10">Loading…</div>;

  return (
    <div data-testid="admin-commissions" className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
          <DollarSign size={12} /> HQ · Commission Ledger
        </div>
        <h1 className="text-3xl font-semibold text-white tracking-tight">Partner commissions</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
        {["proposed","accrued","eligible","approved","paid"].map((s) => (
          <div key={s} data-testid={`admin-comm-stat-${s}`} className="border border-white/10 rounded p-3 bg-white/[0.02]">
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 font-mono">{s}</div>
            <div className="text-lg font-semibold text-white font-mono mt-1">INR {(data.stats[s]?.amount || 0).toLocaleString()}</div>
            <div className="text-[10px] text-white/40 font-mono">{data.stats[s]?.count || 0} rows</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "proposed", "accrued", "eligible", "approved", "scheduled", "paid"].map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)}
                  data-testid={`admin-comm-filter-${f || "all"}`}
                  className={`text-xs uppercase tracking-[0.14em] font-mono px-3 py-1 rounded-full border ${
                    filter === f ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white" : "border-white/10 text-white/50"}`}>
            {f || "all"}
          </button>
        ))}
      </div>

      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {data.items.length === 0 && <div className="p-10 text-center text-white/40">No commission rows.</div>}
        {data.items.map((r) => (
          <div key={r.id} data-testid={`admin-comm-row-${r.id}`} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate font-mono">{r.basis_type} · {r.partner_email}</div>
              <div className="text-[11px] text-white/40 font-mono truncate">
                {r.currency} {r.amount.toLocaleString()} on {r.currency} {r.basis_amount.toLocaleString()} @ {(r.rate*100).toFixed(1)}%
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.14em] font-mono text-white/70 px-2 py-0.5 border border-white/10 rounded">{r.status}</span>
            {(COMMISSION_NEXT[r.status] || []).slice(0,2).map((next) => (
              <Button key={next} onClick={() => transition(r.id, next)}
                      data-testid={`admin-comm-transition-${r.id}-${next}`}
                      className="rounded-full border border-white/15 bg-transparent text-white text-[10px] font-semibold h-auto px-2 py-1">
                → {next}
              </Button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminMarketplace() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title:"", category:"sales", country:"India", scope:"", qualifications:"", estimated_compensation:"", timeline:"", deadline:"" });
  const [applicationsFor, setApplicationsFor] = useState(null);
  const [apps, setApps] = useState([]);

  const load = () => api.get("/admin/marketplace").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const post = async () => {
    try {
      const payload = { ...form, qualifications: form.qualifications.split(",").map(s=>s.trim()).filter(Boolean) };
      await api.post("/admin/marketplace", payload);
      toast.success("Posted.");
      setShow(false);
      setForm({ ...form, title:"", scope:"", qualifications:"", estimated_compensation:"", timeline:"", deadline:"" });
      load();
    } catch { toast.error("Couldn't post."); }
  };

  const openApps = async (opp) => {
    const r = await api.get(`/admin/marketplace/${opp.id}/applications`);
    setApps(r.data.items);
    setApplicationsFor(opp);
  };

  const decide = async (app_id, decision) => {
    try {
      await api.post(`/admin/marketplace/${applicationsFor.id}/decide`, { application_id: app_id, decision });
      toast.success(`${decision}ed.`);
      openApps(applicationsFor);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  return (
    <div data-testid="admin-marketplace" className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
            <Compass size={12} /> HQ · Marketplace
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">Opportunity marketplace</h1>
        </div>
        <Button onClick={() => setShow(!show)} data-testid="admin-mkt-new-btn" className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">
          <Plus size={13} className="mr-1"/> Post opportunity
        </Button>
      </div>

      {show && (
        <div data-testid="admin-mkt-form" className="border border-white/10 rounded-lg p-5 bg-white/[0.02] mb-6 space-y-2">
          <Input data-testid="admin-mkt-title" placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="bg-white/5 border-white/10"/>
          <div className="grid grid-cols-3 gap-2">
            <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} data-testid="admin-mkt-category" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white font-mono">
              {["sales","advertising","location","technology","distribution","regional"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input placeholder="Country (optional)" value={form.country} onChange={(e)=>setForm({...form,country:e.target.value})} className="bg-white/5 border-white/10"/>
            <Input placeholder="Compensation" value={form.estimated_compensation} onChange={(e)=>setForm({...form,estimated_compensation:e.target.value})} className="bg-white/5 border-white/10"/>
          </div>
          <textarea rows={3} placeholder="Scope (what, why, where, how)" value={form.scope} onChange={(e)=>setForm({...form,scope:e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white resize-none"/>
          <Input placeholder="Qualifications (comma-separated)" value={form.qualifications} onChange={(e)=>setForm({...form,qualifications:e.target.value})} className="bg-white/5 border-white/10"/>
          <Button onClick={post} data-testid="admin-mkt-submit" className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">Post</Button>
        </div>
      )}

      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {items.length === 0 && <div className="p-10 text-center text-white/40">No opportunities posted yet.</div>}
        {items.map((m) => (
          <button key={m.id} onClick={() => openApps(m)} data-testid={`admin-mkt-row-${m.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{m.title}</div>
              <div className="text-[11px] text-white/40 font-mono truncate">{m.category} · {m.country || "any"} · {m.applications_count} applications</div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-[0.14em] ${m.status === "open" ? "text-[#CCFF00]" : "text-white/40"}`}>{m.status}</span>
            <ChevronRight size={14} className="text-white/30"/>
          </button>
        ))}
      </div>

      {applicationsFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setApplicationsFor(null)}>
          <div className="bg-[#141110] border border-white/15 rounded-lg max-w-2xl w-full p-6 my-10 space-y-3" onClick={(e)=>e.stopPropagation()}>
            <div className="text-white font-semibold">{applicationsFor.title}</div>
            <div className="text-xs text-white/60">{apps.length} application{apps.length === 1 ? "" : "s"}</div>
            <div className="space-y-2">
              {apps.map((a) => (
                <div key={a.id} data-testid={`mkt-app-${a.id}`} className="border border-white/10 rounded p-3 bg-black/20">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{a.partner_name}</div>
                      <div className="text-[11px] text-white/40 font-mono truncate">{a.partner_email} · {a.partner_tier} · {a.partner_country}</div>
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/60">{a.status}</span>
                  </div>
                  {a.note && <div className="text-xs text-white/70 mt-2 leading-relaxed">{a.note}</div>}
                  {a.status === "applied" && (
                    <div className="flex gap-1 mt-2">
                      <Button onClick={() => decide(a.id, "shortlist")} data-testid={`mkt-decide-${a.id}-shortlist`} className="rounded-full border border-white/15 bg-transparent text-white text-[10px] h-auto px-2 py-1">Shortlist</Button>
                      <Button onClick={() => decide(a.id, "accept")} data-testid={`mkt-decide-${a.id}-accept`} className="rounded-full bg-[#CCFF00] text-black text-[10px] font-semibold h-auto px-2 py-1">Accept</Button>
                      <Button onClick={() => decide(a.id, "decline")} data-testid={`mkt-decide-${a.id}-decline`} className="rounded-full border border-red-400/30 bg-transparent text-red-300 text-[10px] h-auto px-2 py-1">Decline</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
